import { Spinner } from 'reactstrap';
import { CheckboxInput, SelectInput } from '@/shared/ui';
import {
  ADD_NEW_EVENT_TYPE_RHYTHM_EVENTS_TAB_OPTIONS,
  TYPE_RHYTHM_EVENT_ENUM,
} from '@/shared/config';
import EventRangePickers from './eventRangePickers';
import useAddNewEvent from './useAddNewEvent';

export type AddNewEventProps = {
  onCancel: (isOpenNewEvent?: boolean) => void;
  timezone?: any;
  timezoneOffset: number;
  ecgDataMap: any;
  studyId: string;
  profileId: string;
  startReport: any;
  stopReport: any;
};

/**
 * Renders the Add new event modal in the ECG events tab, letting a reviewer pick a rhythm event type and its start and end timestamps. Delegates all state and handlers to useAddNewEvent, shows an extra use-100-percent-available-time checkbox only for AFib, and disables the Add button behind a spinner while the save is in flight.
 *
 * @param props - Modal configuration and callbacks forwarded straight to useAddNewEvent.
 * @returns The add-event modal body with its event-type select, range pickers and Cancel/Add footer.
 */
const AddNewEvent = ({
  onCancel,
  timezone,
  timezoneOffset,
  ecgDataMap,
  studyId,
  profileId,
  startReport,
  stopReport,
}: AddNewEventProps) => {
  const { state, onChangeEvent, onChangeCheckbox, onChange, onClickCancel, onClickAdd } =
    useAddNewEvent({
      onCancel,
      timezone,
      timezoneOffset,
      ecgDataMap,
      studyId,
      profileId,
      startReport,
      stopReport,
    });

  return (
    <div id="add-event">
      <div className="draggable-modal-content">
        <div className="content-title">Add new event</div>
        <div className="content">
          <SelectInput
            className="w-100 mb-3"
            name="eventType"
            label="Event type"
            options={ADD_NEW_EVENT_TYPE_RHYTHM_EVENTS_TAB_OPTIONS}
            placeholder="Select event"
            value={state.eventType}
            onChange={onChangeEvent}
          />
          {state.eventType?.value === TYPE_RHYTHM_EVENT_ENUM.AFIB && (
            <CheckboxInput
              id="isUse100Afib-event"
              checked={state.isUse100Afib}
              className="w-100 mb-3"
              name="isUse100Afib"
              onChange={onChangeCheckbox}
              data={{
                label: 'Use 100% available time',
                value: !state.isUse100Afib as unknown as string, // pre-existing: CheckboxInput's data.value is string|number, this passes a boolean
              }}
            />
          )}
          <EventRangePickers
            startDate={state.startDate}
            endDate={state.endDate}
            startTime={state.startTime}
            endTime={state.endTime}
            onChangePicker={onChange}
            error={state.errorObject}
          />
        </div>
      </div>
      <div className="footer-btn">
        <button
          className="draggable-modal-btn"
          onClick={() => {
            onClickCancel();
          }}
          id="rhythm-event-cancel-add-new-event"
        >
          Cancel
        </button>
        <button
          disabled={state.isSaving}
          className="draggable-modal-btn btn-main"
          onClick={onClickAdd}
          id="rhythm-event-add-new-event"
        >
          {state.isSaving ? (
            // eslint-disable-next-line react/jsx-curly-brace-presence -- must be a literal empty string, not omitted: reactstrap's Spinner defaultProps falls back to children="Loading..." when children is undefined
            <Spinner>{''}</Spinner>
          ) : (
            'Add'
          )}
        </button>
      </div>
    </div>
  );
};

export default AddNewEvent;
